import React, { useEffect, useState, useCallback } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { ja } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import "./DocumentRegister.css";

registerLocale("ja", ja);

// 現在の日付取得 YYYY/MM/DD
const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}/${month}/${day}`;
};

const PatternSystemForm = ({
  onFormDataChange,
  documentCategories = [],
  documentTypes = [],
  currentUserName = "",
  optionsLoading = false,
  resetToken = 0,
}) => {
  // character width計算
  const getCharacterWidth = (text) => {
    if (!text) return 0;
    let width = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const code = char.charCodeAt(0);
      // Check if character is full-width (zenkaku)
      if (
        (code >= 0x3040 && code <= 0x309f) || // Hiragana
        (code >= 0x30a0 && code <= 0x30ff) || // Katakana
        (code >= 0x4e00 && code <= 0x9fff) || // CJK Ideographs
        (code >= 0xff00 && code <= 0xffef) || // Full-width ASCII
        (code >= 0x3000 && code <= 0x303f) // Full-width symbols
      ) {
        width += 2; // Full-width characters count as 2
      } else {
        width += 1; // Half-width characters count as 1
      }
    }
    return width;
  };

  // Convert YYYY/MM/DD string to Date object
  const stringToDate = (dateString) => {
    if (!dateString) return null;
    // Parse YYYY/MM/DD format by splitting
    const parts = dateString.split("/");
    if (parts.length === 3) {
      const [year, month, day] = parts.map(Number);
      const date = new Date(year, month - 1, day);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    return null;
  };

  // Convert Date object to YYYY/MM/DD string
  const dateToString = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}/${month}/${day}`;
  };

  const getInitialFormData = useCallback(
    () => ({
      product_no: "", // 製番
      document_category: "", // 図面種類
      destination: "", // 仕向先
      date: getCurrentDate(), // 日付
      customer: "", // 得意先
      assignee: currentUserName || "", // 担当者
      device_name: "", // 設備名
      remarks: "", // 備考
      assembly_type: "", // 組立種類
    }),
    [currentUserName]
  );

  const [formData, setFormData] = useState(() => getInitialFormData());

  useEffect(() => {
    const resetData = getInitialFormData();
    setFormData(resetData);
    if (onFormDataChange) {
      onFormDataChange(resetData);
    }
  }, [resetToken, currentUserName, onFormDataChange, getInitialFormData]);

  // IME変換中の状態を追跡するためのステート
  const [isComposing, setIsComposing] = useState(false);

  // IME変換の開始処理
  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  // IME変換の終了処理
  const handleCompositionEnd = (e) => {
    setIsComposing(false);
    // Force validation after composition
    validateAndUpdate(e);
  };

  // 検証および更新を行う関数
  const validateAndUpdate = (e) => {
    const { name, value } = e.target;

    // 各フィールドの最大文字数定義
    const fieldMaxWidths = {
      product_no: 10, // 製番 - 全角5文字
      assignee: 20, // 担当者 - 全角10文字
      destination: 40, // 仕向先 - 全角20文字
      customer: 40, // 得意先 - 全角20文字
      device_name: 40, // 設備名 - 全角20文字
      remarks: 40, // 備考 - 全角20文字
      assembly_type: 40, // 組立種類 - 全角20文字
    };

    const maxWidth = fieldMaxWidths[name];
    if (maxWidth) {
      const currentWidth = getCharacterWidth(value);
      if (currentWidth > maxWidth) {
        // 最大文字数に合わせて切り詰める
        let truncatedValue = "";
        let width = 0;
        for (let i = 0; i < value.length; i++) {
          const char = value[i];
          const code = char.charCodeAt(0);
          const charWidth =
            (code >= 0x3040 && code <= 0x309f) || // Hiragana
            (code >= 0x30a0 && code <= 0x30ff) || // Katakana
            (code >= 0x4e00 && code <= 0x9fff) || // CJK Ideographs
            (code >= 0xff00 && code <= 0xffef) || // Full-width ASCII
            (code >= 0x3000 && code <= 0x303f) // Full-width symbols
              ? 2
              : 1;

          if (width + charWidth > maxWidth) break;
          truncatedValue += char;
          width += charWidth;
        }

        const truncatedFormData = {
          ...formData,
          [name]: truncatedValue,
        };
        setFormData(truncatedFormData);

        if (onFormDataChange) {
          onFormDataChange(truncatedFormData);
        }
        return;
      }
    }

    // 通常の更新
    const newFormData = {
      ...formData,
      [name]: value,
    };
    setFormData(newFormData);

    if (onFormDataChange) {
      onFormDataChange(newFormData);
      console.log("PatternSystemForm data:", newFormData);
    }
  };

  // 入力変更処理
  const handleChange = (e) => {
    const { name, value } = e.target;

    // 常に先に値更新
    const newFormData = {
      ...formData,
      [name]: value,
    };
    setFormData(newFormData);

    // IME変換中は検証スキップ
    if (isComposing) {
      return;
    }

    // IME変換以外の変更時に検証を行う
    validateAndUpdate(e);
  };

  // Highlight weekends function
  const highlightWeekends = (date) => {
    const day = date.getDay();
    if (day === 0) {
      return "highlight-sunday";
    } else if (day === 6) {
      return "highlight-saturday";
    }
    return "";
  };

  // Date change handler
  const handleDateChange = (date) => {
    const newFormData = {
      ...formData,
      date: dateToString(date), // Format YYYY/MM/DD
    };
    setFormData(newFormData);

    if (onFormDataChange) {
      onFormDataChange(newFormData);
    }
  };

  //documentCategories
  const drawingTypeOptions = (documentCategories || []).map((item) => ({
    value: String(item.document_category),
    label: item.document_category_text,
  }));

  // Debug
  console.log("PatternSystemForm - documentCategories:", documentCategories);
  console.log("PatternSystemForm - drawingTypeOptions:", drawingTypeOptions);
  console.log("PatternSystemForm - formData:", formData);

  return (
    <div className="pattern-form">
      <div className="form-grid pattern-system-grid">
        {/* 1番目行: 製番 */}
        <div className="form-row">
          <label className="form-label">製番</label>
          <input
            type="text"
            name="product_no"
            value={formData.product_no}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={10}
          />
        </div>

        <div className="form-row form-row-placeholder" aria-hidden="true">
          <label className="form-label">&nbsp;</label>
        </div>

        {/* 2番目行: 仕向先 + 図面ﾀｲﾌﾟ */}
        <div className="form-row">
          <label className="form-label">仕向先</label>
          <input
            type="text"
            name="destination"
            value={formData.destination}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={40}
          />
        </div>

        <div className="form-row">
          <label className="form-label">図面タイプ（システム装置）</label>
          <select
            name="document_category"
            value={formData.document_category}
            onChange={handleChange}
            className="form-select"
            disabled={optionsLoading}
          >
            <option value=""></option>
            {drawingTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* 3番目行: 得意先 + 日付 */}
        <div className="form-row">
          <label className="form-label">得意先</label>
          <input
            type="text"
            name="customer"
            value={formData.customer}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={40}
          />
        </div>

        <div className="form-row">
          <label className="form-label">日付</label>
          <div className="mod-form__text calendar">
            <DatePicker
              selected={stringToDate(formData.date)}
              onChange={handleDateChange}
              dayClassName={highlightWeekends}
              locale="ja"
              dateFormat="yyyy年MM月dd日"
              className="custom-datepicker"
              calendarStartDay={1}
              placeholderText="日付を選択"
              renderCustomHeader={({
                date,
                changeYear,
                changeMonth,
                decreaseMonth,
                increaseMonth,
              }) => {
                const currentYear = new Date().getFullYear();
                const years = Array.from(
                  { length: 30 },
                  (_, i) => currentYear - i
                );

                const months = [
                  "01",
                  "02",
                  "03",
                  "04",
                  "05",
                  "06",
                  "07",
                  "08",
                  "09",
                  "10",
                  "11",
                  "12",
                ];

                return (
                  <div
                    className="custom-header"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "4px 8px",
                    }}
                  >
                    {/* Prev button */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        decreaseMonth();
                      }}
                    >
                      {"<"}
                    </button>

                    {/* Year + Month display + dropdown */}
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <select
                          className="year-select"
                          value={date.getFullYear()}
                          onChange={(e) => changeYear(e.target.value)}
                          style={{ padding: "2px 4px" }}
                        >
                          {years.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                        <span>年</span>
                      </div>

                      <div>
                        <select
                          value={date.getMonth()}
                          onChange={(e) => changeMonth(e.target.value)}
                          style={{ padding: "2px 4px" }}
                          className="month-select"
                        >
                          {months.map((m, i) => (
                            <option key={i} value={i}>
                              {m}
                            </option>
                          ))}
                        </select>
                        <span>月</span>
                      </div>
                    </div>

                    {/* Next button */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        increaseMonth();
                      }}
                    >
                      {">"}
                    </button>
                  </div>
                );
              }}
              onKeyDown={(e) => e.preventDefault()}
            />
          </div>
        </div>

        {/* 4番目行: 設備名 + 担当者 */}
        <div className="form-row">
          <label className="form-label">設備名</label>
          <input
            type="text"
            name="device_name"
            value={formData.device_name}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={40}
          />
        </div>

        <div className="form-row">
          <label className="form-label">担当者</label>
          <input
            type="text"
            name="assignee"
            value={formData.assignee}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={20}
          />
        </div>

        {/* 5番目行: 組立種類 + 備考 */}
        <div className="form-row">
          <label className="form-label">組立種類</label>
          <input
            type="text"
            name="assembly_type"
            value={formData.assembly_type}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={40}
          />
        </div>

        <div className="form-row">
          <label className="form-label">備考</label>
          <input
            type="text"
            name="remarks"
            value={formData.remarks}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="form-input"
            maxLength={40}
          />
        </div>
      </div>
    </div>
  );
};

export default PatternSystemForm;
